import React from 'react';
import type { IExternalAccount, IStripeAccount } from '@medely/types';
import { Box, Heading, InfoBadge, VStack, Text, Button, Label } from '@medely/ui-kit';
import { useProNavigateWithMfaRequirement } from '@mp/contexts';
import { useEditStripeAccount } from '@mp/hooks/useStripeAccount';
import { gateNames } from '@mp/constants';
import { useCurrentUser, useGate } from '@mp/hooks';
import { HelpCenterLink } from '@mp/components/helpCenterLink';

type DirectDepositSectionProps = {
  account: IStripeAccount;
};

type WithDataVariationProps = {
  active: boolean;
  navigateToPayoutMethod: (withAccount: boolean) => void;
  handleClickSetAsActive: VoidFunction;
  isEditing: boolean;
} & DirectDepositSectionProps;

type EmptyStateVariationProps = {
  handleClickSetAsActive: VoidFunction;
  isLoading: boolean;
};

type UpdateDirectDepositButtonProps = {
  isVerifiedExternalAccount: boolean;
  hasSomeExternalAccount: boolean;
  onClick: VoidFunction;
};

function EmptyStateVariation({ handleClickSetAsActive, isLoading }: EmptyStateVariationProps) {
  return (
    <VStack mt={1}>
      <Text size="s">Activate your account to set up a direct deposit method.</Text>
      <Box mt={1}>
        <Button onClick={handleClickSetAsActive} fullWidth loading={isLoading}>
          Set as active
        </Button>
      </Box>
    </VStack>
  );
}

function UpdateDirectDepositButton({
  isVerifiedExternalAccount,
  hasSomeExternalAccount,
  onClick,
}: UpdateDirectDepositButtonProps) {
  if (isVerifiedExternalAccount || !hasSomeExternalAccount) {
    return (
      <Box mt={1}>
        <Button variant="outlined" fullWidth onClick={onClick}>
          Update direct deposit
        </Button>
      </Box>
    );
  }

  return (
    <VStack mt={1} alignItems="center" justifyContent="center">
      <HelpCenterLink text="Update direct deposit" configKey="updateExternalAccountHelpLink" />
    </VStack>
  );
}

function WithDataVariation({
  active,
  navigateToPayoutMethod,
  account,
  handleClickSetAsActive,
  isEditing,
}: WithDataVariationProps) {
  const isVerifiedExternalAccount = useGate(gateNames.verifiedExternalAccount);
  const { currentUser } = useCurrentUser();
  const stripeAccounts = currentUser?.professional?.stripe_accounts ?? [];
  const allExternalAccounts = stripeAccounts
    .flatMap((account) => account.external_accounts || [])
    .filter(Boolean);
  const hasSomeExternalAccount = Boolean(allExternalAccounts?.length);

  const externalAccount = account.external_accounts?.[0] as IExternalAccount;

  function getAccountType(type: string) {
    if (type === 'card') {
      return 'Debit card ';
    }

    return 'Bank account ';
  }

  return (
    <Box>
      {active && (
        <Box mt={1}>
          <Box width={140} mb={0.5}>
            <InfoBadge color="success" label="Active payout account" />
          </Box>

          <Box mt={0.5}>
            <Text size="s">The active payout account receives all payouts.</Text>
          </Box>
        </Box>
      )}

      {externalAccount && (
        <VStack mb={1} mt={active ? 2 : 1}>
          <Label size="s">
            {getAccountType(externalAccount.account_type)}
            ending in {externalAccount.last4}
          </Label>

          {externalAccount.exp_month && (
            <Text size="s">
              Expires {externalAccount.exp_month}/{externalAccount.exp_year}
            </Text>
          )}
        </VStack>
      )}
      {active && (
        <UpdateDirectDepositButton
          isVerifiedExternalAccount={isVerifiedExternalAccount}
          hasSomeExternalAccount={hasSomeExternalAccount}
          onClick={() => navigateToPayoutMethod(true)}
        />
      )}
      {!active && (
        <Box mt={2}>
          <Text size="s">
            {!externalAccount?.id
              ? 'The active payout account receives all payouts.'
              : 'The active payout account receives all payouts. Account details can be edited for active accounts.'}
          </Text>
          <Box mt={1}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleClickSetAsActive}
              loading={isEditing}
            >
              Set as active
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export const DirectDepositSection = ({ account }: DirectDepositSectionProps) => {
  const externalAccount = account.external_accounts?.[0];
  const { navigateWithMfaRequirement } = useProNavigateWithMfaRequirement();
  const { edit, isEditing } = useEditStripeAccount();
  const { active } = account;

  if (!account) {
    navigateWithMfaRequirement('PayoutTaxes');
  }

  function getVariation() {
    if (!externalAccount && !active) {
      return 'empty-state';
    }

    if (!active) {
      return 'inactive';
    }

    return 'active';
  }

  const variation = getVariation();

  function navigateToPayoutMethod(withAccount: boolean) {
    navigateWithMfaRequirement('PayoutMethod', withAccount ? { id: account.id } : {});
  }

  function handleClickSetAsActive() {
    edit({
      id: account.id,
      active: true,
    });
  }

  const variationComponent = {
    'empty-state': (
      <EmptyStateVariation handleClickSetAsActive={handleClickSetAsActive} isLoading={isEditing} />
    ),
    inactive: (
      <WithDataVariation
        active={Boolean(active)}
        navigateToPayoutMethod={navigateToPayoutMethod}
        account={account}
        handleClickSetAsActive={handleClickSetAsActive}
        isEditing={isEditing}
      />
    ),
    active: (
      <WithDataVariation
        active={Boolean(active)}
        navigateToPayoutMethod={navigateToPayoutMethod}
        account={account}
        handleClickSetAsActive={handleClickSetAsActive}
        isEditing={isEditing}
      />
    ),
  };

  return (
    <VStack mt={3}>
      <Heading size="s">Direct deposit</Heading>
      {variationComponent[variation]}
    </VStack>
  );
};
